/**
 * BriefStageRail
 *
 * Batch-progress indicator for a brief's production pipeline:
 * DRAFT -> CAPTURING -> EDITING -> COMPLETE. A compact dot stepper (label
 * under each dot, sub-state in the tooltip) sized for the narrow Concept
 * column (DD-014), using the same status-dot color language as the idea
 * workspace rail. When ``onStatusChange`` is provided each stage is
 * clickable to advance/rewind the brief.
 *
 * Pure progression math lives in ``briefStageState`` so it is unit-tested
 * apart from this chrome.
 */

import React from "react";
import { CheckIcon } from "@/components/icons";
import { BriefStatus } from "../types";
import { deriveBriefStages, briefProgress } from "../briefStageState";

interface BriefStageRailProps {
  status: BriefStatus;
  onStatusChange?: (status: BriefStatus) => void;
  disabled?: boolean;
}

export const BriefStageRail: React.FC<BriefStageRailProps> = ({
  status,
  onStatusChange,
  disabled = false,
}) => {
  const stages = deriveBriefStages(status);
  const progress = briefProgress(status);
  const isInteractive = Boolean(onStatusChange) && !disabled;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-[0.1em]">
          Production pipeline
        </span>
        <span className="text-xs font-medium text-gray-500">
          {progress.completedStages}/{progress.total - 1} stage avanzati
        </span>
      </div>

      {/* Compact dot stepper — fits the narrow Concept column (DD-014);
          no bordered stage boxes (DD-011). Sub-state lives in the tooltip. */}
      <ol className="flex items-start">
        {stages.map((stage, index) => {
          const subState = stage.current
            ? "In corso"
            : stage.done
              ? "Completato"
              : "In attesa";
          const inner = (
            <>
              <span
                className={`flex items-center justify-center w-7 h-7 rounded-full transition-colors ${
                  stage.reached ? stage.dotColor : "bg-gray-200"
                } ${stage.current ? "ring-2 ring-blue-200 ring-offset-2" : ""}`}
              >
                {stage.done ? (
                  <CheckIcon className="w-4 h-4 text-white" />
                ) : (
                  <span
                    className={`text-[11px] font-bold ${
                      stage.reached ? "text-white" : "text-gray-500"
                    }`}
                  >
                    {index + 1}
                  </span>
                )}
              </span>
              <span
                className={`block w-full text-center text-[10px] font-medium truncate ${
                  stage.reached ? "text-gray-700" : "text-gray-400"
                }`}
              >
                {stage.label}
              </span>
            </>
          );

          return (
            <React.Fragment key={stage.id}>
              {index > 0 && (
                <span
                  className="flex-1 h-px bg-gray-200 mt-3.5 min-w-2"
                  aria-hidden
                />
              )}
              <li className="shrink-0 max-w-20">
                {isInteractive ? (
                  <button
                    type="button"
                    onClick={() => onStatusChange?.(stage.id)}
                    title={`${stage.label}: ${subState}`}
                    className="flex flex-col items-center gap-1.5 w-full min-w-0 group"
                  >
                    {inner}
                  </button>
                ) : (
                  <div
                    title={`${stage.label}: ${subState}`}
                    className="flex flex-col items-center gap-1.5 w-full min-w-0"
                  >
                    {inner}
                  </div>
                )}
              </li>
            </React.Fragment>
          );
        })}
      </ol>
    </div>
  );
};
